import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/services/api'
import { authStateManager } from '@/utils/authStateManager'
import { tokenManager } from '@/utils/tokenManager'

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  phone?: string
  membership_level: string
  avatar_url?: string
  status: string
  balance: number
  carbon_credits: number
  birth_date?: string
  questionnaire_completed?: boolean
  carbon_footprint_score?: number
  carbon_footprint_breakdown?: Record<string, number>
  questionnaire_completed_at?: string
  created_at: string
  updated_at: string
}

export interface UserAgreement {
  id: number
  title: string
  content: string
  version: string
  is_active: boolean
}

export interface RegisterData {
  email: string
  password: string
  password_confirmation: string
  first_name: string
  last_name: string
  phone?: string
  birth_date?: string
  invitation_code?: string
  agreement_consent: boolean
  agreement_id: number
}

export interface LoginCredentials {
  email: string
  password: string
  remember?: boolean
}

export interface AuthResponse {
  user: User
  token: string
  token_type: string
  expires_in: number
}

export const useAuthStore = defineStore('auth', () => {
  // 使用认证状态管理器的响应式状态
  const token = authStateManager.token
  const user = authStateManager.user
  const loading = ref(false)
  const agreement = ref<UserAgreement | null>(null)

  // 从认证状态管理器获取认证状态
  const isAuthenticated = authStateManager.isAuthenticated
  const isInitializing = authStateManager.isInitializing
  const lastError = authStateManager.lastError

  const fullName = computed(() =>
    user.value ? `${user.value.first_name} ${user.value.last_name}` : ''
  )

  // Set authentication data
  const setAuth = (authData: AuthResponse) => {
    // 使用认证状态管理器设置认证数据
    authStateManager.setAuthData(authData.user, authData.token, authData.expires_in)

    // 重置问卷完成状态缓存，让系统重新检查
    questionnaireCompleted.value = null

    console.log('认证数据已设置', {
      userId: authData.user.id,
      questionnaire_completed: authData.user.questionnaire_completed,
      carbon_footprint_score: authData.user.carbon_footprint_score
    })
  }

  // Clear authentication data
  const clearAuth = () => {
    // 清除localStorage中的问卷完成状态（在清除用户数据之前）
    if (user.value?.id) {
      localStorage.removeItem(`questionnaire_completed_${user.value.id}`)
      localStorage.removeItem(`questionnaire_completed_at_${user.value.id}`)
    }

    // 使用认证状态管理器清除认证状态
    authStateManager.clearAuthState()
    // 清除问卷完成状态缓存
    questionnaireCompleted.value = null
  }

  // Initialize auth from stored token (非阻塞式)
  const initAuth = async (): Promise<void> => {
    console.log('[AuthStore] 开始非阻塞式认证初始化')

    try {
      // 使用认证状态管理器进行非阻塞初始化
      await authStateManager.initializeAuth()
      console.log('[AuthStore] 认证初始化完成')
    } catch (error) {
      // 认证初始化失败不应该阻塞应用
      console.warn('[AuthStore] 认证初始化失败，但不阻塞应用:', error)

      // 确保应用可以继续运行
      authStateManager.clearAuthState()
    }
  }

  // Get user agreement
  const fetchAgreement = async (): Promise<UserAgreement | null> => {
    try {
      const response = await apiClient.get<{ agreement: UserAgreement }>('/auth/agreement')
      if (response.success) {
        agreement.value = response.data.agreement
        return agreement.value
      }
      return null
    } catch (error) {
      console.error('Failed to fetch agreement:', error)
      return null
    }
  }

  // Register user
  const register = async (registerData: RegisterData) => {
    loading.value = true
    try {
      const response = await apiClient.post<AuthResponse>('/auth/register', registerData)

      if (response.success) {
        setAuth(response.data)
        return { success: true, data: response.data }
      }

      return {
        success: false,
        message: response.message || 'Registration failed',
        errors: {}
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Registration failed',
        errors: error.details?.errors || {}
      }
    } finally {
      loading.value = false
    }
  }

  // Check if user has completed questionnaire
  const checkQuestionnaireStatus = async (): Promise<boolean> => {
    // 如果用户未认证，返回false
    if (!isAuthenticated.value || !user.value) {
      console.log('用户未认证，问卷状态为false')
      return false
    }

    // 首先检查服务器返回的用户对象中的questionnaire_completed字段（最权威的数据源）
    if (user.value && user.value.questionnaire_completed === true) {
      questionnaireCompleted.value = true
      console.log('用户问卷状态：已完成（来自服务器用户对象）', {
        questionnaire_completed: user.value.questionnaire_completed,
        carbon_footprint_score: user.value.carbon_footprint_score,
        completed_at: user.value.questionnaire_completed_at
      })

      // 同步更新 localStorage
      localStorage.setItem(`questionnaire_completed_${user.value.id}`, 'true')
      if (user.value.questionnaire_completed_at) {
        localStorage.setItem(`questionnaire_completed_at_${user.value.id}`, user.value.questionnaire_completed_at)
      }

      return true
    }

    // 如果服务器显示未完成，清理可能存在的本地缓存
    const localCompletedStatus = localStorage.getItem(`questionnaire_completed_${user.value.id}`)
    if (localCompletedStatus === 'true') {
      console.log('服务器显示问卷未完成，但本地缓存显示已完成，清理本地缓存')
      localStorage.removeItem(`questionnaire_completed_${user.value.id}`)
      localStorage.removeItem(`questionnaire_completed_at_${user.value.id}`)
    }

    console.log('本地状态显示未完成，检查API...', {
      questionnaire_completed: user.value?.questionnaire_completed,
      user_id: user.value?.id
    })

    try {
      // 通过API检查最新状态
      console.log('通过API检查问卷状态...')
      const response = await apiClient.get<{ responses: any[] }>('/questionnaire/history')

      console.log('问卷历史API响应:', response)

      if (response.success) {
        const responses = (response as any).data.responses
        // 检查是否有完成的问卷记录（必须是已完成的记录）
        const hasCompleted = responses && responses.length > 0 &&
          responses.some((resp: any) => resp.completed_at !== null)

        // 更新本地缓存
        questionnaireCompleted.value = hasCompleted

        // 如果API显示未完成，但本地显示已完成，清理本地错误数据
        if (!hasCompleted && user.value && user.value.questionnaire_completed) {
          console.log('API显示问卷未完成，但本地显示已完成，清理本地错误数据')
          const userId = user.value.id
          
          // 清理localStorage
          localStorage.removeItem(`questionnaire_completed_${userId}`)
          localStorage.removeItem(`questionnaire_completed_at_${userId}`)
          localStorage.removeItem(`carbon_footprint_score_${userId}`)
          
          // 清理用户对象
          user.value.questionnaire_completed = false
          delete user.value.carbon_footprint_score
          delete user.value.questionnaire_completed_at
          delete user.value.carbon_footprint_breakdown
          
          // 更新认证状态管理器
          authStateManager.updateUserData(user.value)
        }

        // 如果API显示已完成，检查并更新用户对象中的数据
        if (hasCompleted && user.value) {
          const latestResponse = responses.find((r: any) => r.completed_at !== null)
          if (latestResponse) {
            let needsUpdate = false

            // 检查是否需要更新问卷完成状态
            if (!user.value.questionnaire_completed) {
              user.value.questionnaire_completed = true
              needsUpdate = true
            }

            // 检查是否需要更新碳足迹分数
            if (!user.value.carbon_footprint_score && latestResponse.total_carbon_footprint) {
              user.value.carbon_footprint_score = latestResponse.total_carbon_footprint
              needsUpdate = true
            }

            // 检查是否需要更新分类数据
            if (!user.value.carbon_footprint_breakdown && latestResponse.calculations?.by_category) {
              user.value.carbon_footprint_breakdown = latestResponse.calculations.by_category
              needsUpdate = true
            }

            // 检查是否需要更新完成时间
            if (!user.value.questionnaire_completed_at && latestResponse.completed_at) {
              user.value.questionnaire_completed_at = latestResponse.completed_at
              needsUpdate = true
            }

            if (needsUpdate) {
              // 持久化到 localStorage
              localStorage.setItem(`questionnaire_completed_${user.value.id}`, 'true')
              localStorage.setItem(`questionnaire_completed_at_${user.value.id}`, latestResponse.completed_at)
              if (latestResponse.total_carbon_footprint) {
                localStorage.setItem(`carbon_footprint_score_${user.value.id}`, latestResponse.total_carbon_footprint.toString())
              }

              // 更新认证状态管理器中的用户数据
              authStateManager.updateUserData(user.value)

              console.log('从API同步用户问卷数据', {
                userId: user.value.id,
                score: user.value.carbon_footprint_score,
                completed_at: user.value.questionnaire_completed_at,
                breakdown: user.value.carbon_footprint_breakdown
              })
            }
          }
        }

        console.log('用户问卷状态检查结果：', {
          hasCompleted,
          responsesCount: responses?.length || 0,
          completedResponses: responses?.filter((r: any) => r.completed_at !== null).length || 0
        })

        return hasCompleted
      }
      return false
    } catch (error: any) {
      console.error('Failed to check questionnaire status:', error)

      // 如果是401错误，抛出以便路由守卫处理
      if (error?.status === 401) {
        throw error
      }

      // 其他错误，返回false但不阻塞
      return false
    }
  }

  // 问卷完成状态缓存
  const questionnaireCompleted = ref<boolean | null>(null)

  // 简单的数据修复方法
  const fixDataInconsistency = () => {
    if (!user.value?.id) return false

    const userId = user.value.id
    const localCompleted = localStorage.getItem(`questionnaire_completed_${userId}`) === 'true'
    const localScore = localStorage.getItem(`carbon_footprint_score_${userId}`)
    const localCompletedAt = localStorage.getItem(`questionnaire_completed_at_${userId}`)

    let fixed = false

    // 如果本地有完成状态但用户对象中没有，恢复完成状态
    if (localCompleted && !user.value.questionnaire_completed) {
      console.log('从本地缓存恢复问卷完成状态')
      user.value.questionnaire_completed = true
      fixed = true
    }

    // 如果本地有分数但用户对象中没有，恢复分数（即使questionnaire_completed已经是true）
    if (localScore && !user.value.carbon_footprint_score) {
      console.log('从本地缓存恢复碳足迹分数:', localScore)
      user.value.carbon_footprint_score = parseFloat(localScore)
      fixed = true
    }

    // 如果本地有完成时间但用户对象中没有，恢复完成时间
    if (localCompletedAt && !user.value.questionnaire_completed_at) {
      console.log('从本地缓存恢复问卷完成时间:', localCompletedAt)
      user.value.questionnaire_completed_at = localCompletedAt
      fixed = true
    }

    return fixed
  }

  // Mark questionnaire as completed (for cache invalidation)
  const markQuestionnaireCompleted = async (carbonFootprintData?: any) => {
    // 设置本地缓存标志，避免重复检查
    questionnaireCompleted.value = true

    // 同时更新用户对象中的状态
    if (user.value) {
      user.value.questionnaire_completed = true
      user.value.questionnaire_completed_at = new Date().toISOString()

      // 如果提供了碳足迹数据，也更新这些信息
      if (carbonFootprintData) {
        user.value.carbon_footprint_score = carbonFootprintData.total_carbon_footprint
        user.value.carbon_footprint_breakdown = carbonFootprintData.category_breakdown || {}
      }

      // 持久化到 localStorage 中
      localStorage.setItem(`questionnaire_completed_${user.value.id}`, 'true')
      localStorage.setItem(`questionnaire_completed_at_${user.value.id}`, user.value.questionnaire_completed_at)

      // 同时更新认证状态管理器中的用户数据，确保持久化
      authStateManager.updateUserData(user.value)

      // 注意：服务器端应该在问卷提交成功后自动更新用户的 questionnaire_completed 状态
      // 这里我们只更新本地状态，服务器状态由问卷提交 API 负责更新
      console.log('本地用户状态已更新，服务器状态应该由问卷提交 API 自动更新')
    }

    console.log('问卷标记为已完成并已持久化')
  }

  // Login user
  const login = async (credentials: LoginCredentials) => {
    loading.value = true
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', {
        email: credentials.email,
        password: credentials.password
      })

      if (response.success) {
        setAuth(response.data)

        // 检查用户是否完成了问卷
        const hasCompletedQuestionnaire = await checkQuestionnaireStatus()

        return {
          success: true,
          hasCompletedQuestionnaire
        }
      }

      return {
        success: false,
        message: response.message || 'Login failed'
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Login failed'
      }
    } finally {
      loading.value = false
    }
  }

  // Logout user
  const logout = async () => {
    loading.value = true
    try {
      if (token.value) {
        await apiClient.post('/auth/logout')
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAuth()
      loading.value = false
    }
  }

  // Refresh token
  const refreshToken = async (): Promise<boolean> => {
    try {
      console.log('[AuthStore] 尝试刷新令牌')

      const response = await apiClient.post<AuthResponse>('/auth/refresh')
      if (response.success && response.data) {
        console.log('[AuthStore] 令牌刷新成功')

        // 使用认证状态管理器设置新的认证数据
        authStateManager.setAuthData(
          response.data.user,
          response.data.token,
          response.data.expires_in
        )
        return true
      }

      console.warn('[AuthStore] 令牌刷新失败：服务器响应无效')
      return false
    } catch (error) {
      console.error('[AuthStore] 令牌刷新失败:', error)
      clearAuth()
      return false
    }
  }

  // Forgot password
  const forgotPassword = async (email: string) => {
    loading.value = true
    try {
      const response = await apiClient.post<{ message: string }>('/auth/forgot-password', { email })
      if (response.success) {
        return { success: true, message: response.message }
      }
      throw new Error(response.message || 'Failed to send reset email')
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Failed to send reset email'
      }
    } finally {
      loading.value = false
    }
  }

  // Reset password
  const resetPassword = async (resetData: {
    token: string
    email: string
    password: string
    password_confirmation: string
  }) => {
    loading.value = true
    try {
      const response = await apiClient.post<{ message: string }>('/auth/reset-password', resetData)
      if (response.success) {
        return { success: true, message: response.message }
      }
      throw new Error(response.message || 'Password reset failed')
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Password reset failed'
      }
    } finally {
      loading.value = false
    }
  }

  // 获取认证状态管理器的调试信息
  const getAuthDebugInfo = () => {
    return authStateManager.getDebugInfo()
  }

  // 检查令牌是否需要刷新
  const shouldRefreshToken = () => {
    return tokenManager.shouldRefreshToken()
  }

  // 获取令牌剩余时间
  const getTokenRemainingTime = () => {
    return tokenManager.getTokenRemainingTime()
  }

  // 获取当前用户信息（刷新用户状态）
  const fetchCurrentUser = async () => {
    try {
      if (!token.value) {
        throw new Error('No authentication token')
      }

      // 重新检查问卷状态以确保状态同步
      await checkQuestionnaireStatus()

      console.log('用户信息已刷新')
      return user.value
    } catch (error) {
      console.error('获取用户信息失败:', error)
      throw error
    }
  }

  return {
    token,
    user,
    loading,
    agreement,
    isAuthenticated,
    isInitializing,
    lastError,
    fullName,
    setAuth,
    clearAuth,
    initAuth,
    fetchAgreement,
    register,
    login,
    logout,
    refreshToken,
    forgotPassword,
    resetPassword,
    checkQuestionnaireStatus,
    markQuestionnaireCompleted,
    fixDataInconsistency,
    getAuthDebugInfo,
    shouldRefreshToken,
    getTokenRemainingTime,
    fetchCurrentUser,
  }
})